'use client';

import React, { useState, useEffect, useRef } from 'react';
import { useCurrency } from '@/hooks/useCurrency';
import { useForm } from 'react-hook-form';
import SubTitle from '@/components/common/SubTitle';
import CounterButton from '@/components/common/CounterButton';
import CustomButton from '@/components/common/CustomButton';
import { DatePicker } from '@/components/common/DatePicker';
import { DropdownDatePicker } from '@/components/common/DropdownDatePicker';
import { ReadOnlyDateDisplay } from '@/components/common/ReadOnlyDateDisplay';
import CustomInput from '@/components/common/CustomInput';
import Border from '@/components/common/border';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useUserStore } from '@/store/userStore';
import { useAddActivityBooking } from '@/hooks/useAddActivityBooking';
import { useAddPackageBooking } from '@/hooks/useAddPackageBooking';
import CustomToast from '@/components/common/CustomToast';
import { useCurrencyStore } from '@/store/currencyStore';
import CurrencyDisplay from '@/components/common/CurrencyDisplay';
import { useBookingStore } from '@/store/bookingStore';
import { useRouter } from 'next/router';
import { countries } from '@/constants/countries';
import { CountryCodeSelect } from '@/components/common/CountryCodeSelect';
import TermsAndConditionsContent from '@/components/common/TermsAndConditionsContent';
import { ExperienceType } from './types';
import { typography } from '@/design-system/tokens/typography';
import {
  getNearestAvailableBookingDate,
  getPackageDatePricingStatus,
  isPackageAvailableDatesMode,
  isPastPackageDate,
  isValidPackageDate,
  resolvePackageAvailabilityCalendar,
  startOfLocalDay,
} from '@/utils/packageAvailabilityCalendar';

interface BookingFormValues {
  date?: Date;
  startDate?: Date;
  endDate?: Date;
  adults?: number;
  children?: number;
  userName?: string;
  userEmail?: string;
  userMobile?: string;
  countryCode?: string;
  phoneDigits?: string;
  freshworksGuestId?: string;
  userId?: string;
  acceptTerms?: boolean;
}

interface FloatingBookingFormProps {
  experience: unknown;
  type: ExperienceType;
  /** @deprecated Booking no longer requires SSO login; kept optional for call-site compatibility */
  setIsBookingLoginRequired?: (value: boolean) => void;
  /** Set when form is inside a bottom sheet (e.g. mobile) so country selector opens upward */
  inBottomSheet?: boolean;
  /** Reports the current total traveler count (adults + children) so the page header/bottom bar can reflect group vs standard pricing */
  onTravelerCountChange?: (count: number) => void;
}

export const FloatingBookingForm: React.FC<FloatingBookingFormProps> = ({
  experience,
  type,
  inBottomSheet = false,
  onTravelerCountChange,
}) => {
  const router = useRouter();
  const hasBookingIdInUrl = Boolean(router.query?.booking);
  const { name, email, id, freshworksGuestId, mobile, setMobile } =
    useUserStore();
  const { addBooking: addActivityBooking, loading: activityLoading } =
    useAddActivityBooking();
  const { addBooking: addPackageBooking, loading: packageLoading } =
    useAddPackageBooking();
  const loading = type === 'package' ? packageLoading : activityLoading;
  const addBooking =
    type === 'package' ? addPackageBooking : addActivityBooking;
  const { convertPrice, currencyCode: userCurrencyCode } = useCurrency();
  const [showUserDetails, setShowUserDetails] = useState(false);
  const userDetailsRef = useRef<HTMLDivElement | null>(null);
  const [dateStatus, setDateStatus] = useState('allowed');
  const [showToast, setShowToast] = useState(false);
  const [toastConfig, setToastConfig] = useState<{
    message: string;
    type: 'success' | 'error';
  }>({
    message: '',
    type: 'success',
  });
  const { restore, setRestore } = useBookingStore();
  const { currencySymbol, currencyCode } = useCurrencyStore();
  const formRef = useRef<HTMLDivElement>(null);
  const initialTopRef = useRef<number | null>(null);
  const hasRestoredDraftRef = useRef(false);
  const [isSticky, setIsSticky] = useState(false);
  const [topOffset, setTopOffset] = useState(0);
  const [bottomOffset, setBottomOffset] = useState<number | undefined>(
    undefined
  );
  const footerRef = useRef<HTMLElement | null>(null);
  const prevTopOffsetRef = useRef<number>(100);
  const prevBottomOffsetRef = useRef<number | undefined>(undefined);
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

  const splitPhoneNumber = (fullNumber: string) => {
    if (!fullNumber) return { code: '+91', digits: '' };
    const matchedCountry = countries.find(c => fullNumber.startsWith(c.code));
    if (matchedCountry) {
      return {
        code: matchedCountry.code,
        digits: fullNumber.slice(matchedCountry.code.length),
      };
    }
    return { code: '+91', digits: fullNumber.replace(/^\+/, '') };
  };

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm<BookingFormValues>({
    defaultValues: {
      date: undefined,
      startDate: undefined,
      endDate: undefined,
      adults: 1,
      children: 0,
      userName: name && name !== 'User Name' ? name : '',
      userEmail: email && email !== 'user@example.com' ? email : '',
      userMobile: mobile || '',
      countryCode: splitPhoneNumber(mobile || '').code,
      phoneDigits: splitPhoneNumber(mobile || '').digits,
      freshworksGuestId: freshworksGuestId || '',
      userId: id || '',
      acceptTerms: false,
    },
  });

  useEffect(() => {
    if (name && name !== 'User Name') {
      const currentDate = watch('date');
      const currentStartDate = watch('startDate');
      const currentEndDate = watch('endDate');
      const currentAdults = watch('adults');
      const currentChildren = watch('children');
      const currentMobile = watch('userMobile') || mobile || '';
      const currentAcceptTerms = watch('acceptTerms');
      const { code, digits } = splitPhoneNumber(currentMobile);
      reset({
        date: currentDate || undefined,
        startDate: currentStartDate || undefined,
        endDate: currentEndDate || undefined,
        adults: currentAdults || 1,
        children: currentChildren || 0,
        userName: name,
        userEmail: email,
        userMobile: currentMobile,
        countryCode: code,
        phoneDigits: digits,
        freshworksGuestId,
        userId: id,
        acceptTerms: currentAcceptTerms ?? false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, email, id, freshworksGuestId, mobile, reset]);

  // After login redirect: allow restore when a draft exists so dates (and form) get populated.
  // Only run the "allow restore" flip once per mount to avoid toggling restore in a loop.
  useEffect(() => {
    if (!hasBookingIdInUrl || !experience || typeof experience !== 'object')
      return;
    const booking = (experience as { booking?: Record<string, unknown> })
      .booking;
    if (!booking || typeof booking !== 'object') return;

    const stayBooking =
      typeof booking.stayBooking === 'object' && booking.stayBooking !== null
        ? (booking.stayBooking as {
            checkIn?: unknown;
            checkOut?: unknown;
          })
        : undefined;
    const start =
      typeof stayBooking?.checkIn === 'string'
        ? stayBooking.checkIn
        : booking.bookingStartDate;
    const end =
      typeof stayBooking?.checkOut === 'string'
        ? stayBooking.checkOut
        : booking.bookingEndDate;
    const bookingDate = booking.bookingDate;
    const numAdults = booking.numberOfAdults;
    const numChildren = booking.numberOfChildren;

    if (type === 'activity') {
      const activityDateSource =
        typeof bookingDate === 'string'
          ? bookingDate
          : typeof start === 'string'
            ? start
            : undefined;
      if (activityDateSource) {
        const d = new Date(activityDateSource);
        if (!isNaN(d.getTime())) setValue('date', d);
      }
    }

    if (typeof start === 'string') {
      const d = new Date(start);
      if (!isNaN(d.getTime())) setValue('startDate', d);
    }
    if (typeof end === 'string') {
      const d = new Date(end);
      if (!isNaN(d.getTime())) setValue('endDate', d);
    }
    if (typeof numAdults === 'number' && numAdults >= 0) {
      setValue('adults', numAdults);
    }
    if (typeof numChildren === 'number' && numChildren >= 0) {
      setValue('children', numChildren);
    }
  }, [hasBookingIdInUrl, experience, setValue, type]);

  // Restore draft from localStorage (skip when viewing existing booking with data from API)
  useEffect(() => {
    if (restore) return;
    if (hasBookingIdInUrl && experience && typeof experience === 'object') {
      const booking = (experience as { booking?: unknown }).booking;
      if (booking != null) return;
    }
    try {
      const bookingFormdata = localStorage.getItem('bookingFormDraft');
      if (!bookingFormdata) return;

      hasRestoredDraftRef.current = true;
      const parsedBookingFormdata = JSON.parse(bookingFormdata);
      if (parsedBookingFormdata?.adults) {
        setValue('adults', Number(parsedBookingFormdata.adults));
      }
      if (typeof parsedBookingFormdata?.children !== 'undefined') {
        setValue('children', Number(parsedBookingFormdata.children));
      }
      if (type === 'stay' || type === 'package') {
        if (parsedBookingFormdata?.startDate) {
          const restoredStartDate = new Date(parsedBookingFormdata.startDate);
          if (!isNaN(restoredStartDate.getTime())) {
            setValue('startDate', restoredStartDate);
            clearErrors('startDate');
          }
        }
        if (parsedBookingFormdata?.endDate) {
          const restoredEndDate = new Date(parsedBookingFormdata.endDate);
          if (!isNaN(restoredEndDate.getTime())) {
            setValue('endDate', restoredEndDate);
            clearErrors('endDate');
          }
        }
      } else {
        if (parsedBookingFormdata?.date) {
          const restoredDate = new Date(parsedBookingFormdata.date);
          if (!isNaN(restoredDate.getTime())) {
            setValue('date', restoredDate);
            clearErrors('date');
          }
        }
      }
      setRestore(true);
      setTimeout(() => {
        localStorage.removeItem('bookingFormDraft');
      }, 3000);
    } catch (e) {
      console.error('Failed to restore draft:', e);
    }
  }, [
    restore,
    setValue,
    clearErrors,
    setRestore,
    type,
    hasBookingIdInUrl,
    experience,
  ]);

  // Scroll to user details when shown (desktop only)
  useEffect(() => {
    if (
      showUserDetails &&
      userDetailsRef.current &&
      window.innerWidth >= 1024
    ) {
      userDetailsRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [showUserDetails]);

  // Recalculate position when form expands (user details shown)
  useEffect(() => {
    if (typeof window === 'undefined' || !formRef.current || !isSticky) return;

    // When form expands, trigger a position recalculation
    const recalculatePosition = () => {
      if (!formRef.current || !footerRef.current) return;

      const footerRect = footerRef.current.getBoundingClientRect();
      const formHeight = formRef.current.offsetHeight;
      const footerTop = footerRect.top;
      const minGap = 20;

      // Calculate if form would overlap with footer at current position
      const currentTop = parseInt(
        window.getComputedStyle(formRef.current).top || '80'
      );
      const formBottom = currentTop + formHeight;

      if (formBottom > footerTop - minGap) {
        // Form would overlap, adjust position
        const maxTopOffset = Math.max(0, footerTop - formHeight - minGap);
        const newTopOffset = Math.min(80, maxTopOffset);
        const finalTopOffset = Math.max(0, newTopOffset);
        setTopOffset(finalTopOffset);
        prevTopOffsetRef.current = finalTopOffset;

        // Also set max height
        const availableHeight = footerTop - finalTopOffset - minGap;
        const maxPossibleHeight = window.innerHeight - finalTopOffset - minGap;
        const calculatedMaxHeight = Math.max(
          200,
          Math.min(availableHeight, maxPossibleHeight)
        );
        setMaxHeight(calculatedMaxHeight);
      }
    };

    // Use setTimeout to allow DOM to update with new height
    const timeoutId = setTimeout(recalculatePosition, 100);

    return () => clearTimeout(timeoutId);
  }, [showUserDetails, isSticky]);

  // Calculate initial position for sticky form
  useEffect(() => {
    if (typeof window === 'undefined' || !formRef.current) return;

    const calculateInitialPosition = () => {
      if (!formRef.current) return;
      const rect = formRef.current.getBoundingClientRect();
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      initialTopRef.current = rect.top + scrollTop;
    };

    const timeoutId = setTimeout(calculateInitialPosition, 100);

    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setIsSticky(false);
        initialTopRef.current = null;
      } else {
        calculateInitialPosition();
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, [experience]);

  // Find footer element on mount
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const findFooter = () => {
      // Try to find footer by tag name or common selectors
      const footer = document.querySelector('footer');
      if (footer) {
        footerRef.current = footer as HTMLElement;
      }
    };

    // Try immediately
    findFooter();

    // Also try after a delay to catch dynamically rendered footers
    const timeoutId = setTimeout(findFooter, 500);

    // Use MutationObserver to catch footer when it's added
    const observer = new MutationObserver(findFooter);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, []);

  // Handle sticky positioning for desktop only
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let ticking = false;

    const handleScroll = () => {
      if (!formRef.current || !initialTopRef.current) return;

      if (window.innerWidth < 1024) {
        setIsSticky(false);
        setMaxHeight(undefined);
        return;
      }

      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollTop =
            window.pageYOffset || document.documentElement.scrollTop;
          const shouldBeSticky = scrollTop > initialTopRef.current! - 80;

          // Calculate positioning - prevent form from entering footer
          let calculatedTopOffset = 80;
          let calculatedMaxHeight: number | undefined = undefined;

          // When footer is in view, prevent form from overlapping with it
          if (footerRef.current && shouldBeSticky && formRef.current) {
            const footerRect = footerRef.current.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const footerTop = footerRect.top;
            const formHeight = formRef.current.offsetHeight;

            // Minimum gap between form and footer
            const minGap = 20;

            // Check if footer is visible in viewport
            const footerInView = footerTop < viewportHeight && footerTop > 0;

            if (footerInView) {
              // Calculate where the bottom of the form would be with default top offset
              const formBottomAtDefaultTop = 100 + formHeight;

              // Check if form would overlap with footer (including minimum gap)
              const wouldOverlap = formBottomAtDefaultTop > footerTop - minGap;

              if (wouldOverlap) {
                // Calculate the maximum top offset that keeps form above footer
                // footerTop - formHeight - minGap = maximum top position
                const maxTopOffset = Math.max(
                  0,
                  footerTop - formHeight - minGap
                );

                // Use the smaller of default (80) or maxTopOffset to ensure form stays above footer
                calculatedTopOffset = Math.min(80, maxTopOffset);

                // Ensure we don't go negative or too small
                calculatedTopOffset = Math.max(0, calculatedTopOffset);
              } else {
                // Footer visible but no overlap, use default
                calculatedTopOffset = 80;
              }

              // Calculate max height to ensure form doesn't exceed available space
              const availableHeight = footerTop - calculatedTopOffset - minGap;
              const maxPossibleHeight =
                viewportHeight - calculatedTopOffset - minGap;
              calculatedMaxHeight = Math.max(
                200,
                Math.min(availableHeight, maxPossibleHeight)
              );
            } else {
              // Footer not in view, use default top offset
              calculatedTopOffset = 80;
              // No max height constraint when footer not visible
              calculatedMaxHeight = undefined;
            }
          } else {
            // Default top offset when footer not found or not sticky
            calculatedTopOffset = 80;
            calculatedMaxHeight = undefined;
          }

          // Update state
          setIsSticky(prevSticky => {
            if (prevSticky !== shouldBeSticky) {
              if (shouldBeSticky) {
                setTopOffset(calculatedTopOffset);
                setBottomOffset(undefined);
                setMaxHeight(calculatedMaxHeight);
                prevTopOffsetRef.current = calculatedTopOffset;
                prevBottomOffsetRef.current = undefined;
              } else {
                setTopOffset(0);
                setBottomOffset(undefined);
                setMaxHeight(undefined);
                prevTopOffsetRef.current = 0;
                prevBottomOffsetRef.current = undefined;
              }
              return shouldBeSticky;
            } else if (prevSticky && shouldBeSticky) {
              // Update top offset smoothly
              const topOffsetChanged =
                Math.abs(prevTopOffsetRef.current - calculatedTopOffset) > 1;

              if (topOffsetChanged) {
                setTopOffset(calculatedTopOffset);
                prevTopOffsetRef.current = calculatedTopOffset;
              }

              // Always update max height when footer is visible
              setMaxHeight(calculatedMaxHeight);
            }
            return prevSticky;
          });

          ticking = false;
        });

        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  // Monitor form height changes and adjust position accordingly
  useEffect(() => {
    if (typeof window === 'undefined' || !formRef.current || !isSticky) return;

    const resizeObserver = new ResizeObserver(() => {
      // Trigger a position recalculation when form height changes
      if (!formRef.current || !footerRef.current) return;

      const footerRect = footerRef.current.getBoundingClientRect();
      const formHeight = formRef.current.offsetHeight;
      const footerTop = footerRect.top;
      const minGap = 20;

      // Get current top position
      const currentTop = parseInt(
        window.getComputedStyle(formRef.current).top || '100'
      );
      const formBottom = currentTop + formHeight;

      // Check if form would overlap with footer
      if (footerTop > 0 && footerTop < window.innerHeight) {
        // Footer is visible
        if (formBottom > footerTop - minGap) {
          // Form would overlap, adjust position
          const maxTopOffset = Math.max(0, footerTop - formHeight - minGap);
          const newTopOffset = Math.min(100, maxTopOffset);
          const finalTopOffset = Math.max(0, newTopOffset);

          // Only update if significantly different
          if (Math.abs(prevTopOffsetRef.current - finalTopOffset) > 1) {
            setTopOffset(finalTopOffset);
            prevTopOffsetRef.current = finalTopOffset;
          }
        }

        // Calculate and set max height
        const currentTopForCalc =
          Math.abs(prevTopOffsetRef.current - 0) < 1
            ? 100
            : prevTopOffsetRef.current;
        const availableHeight = footerTop - currentTopForCalc - minGap;
        const maxPossibleHeight =
          window.innerHeight - currentTopForCalc - minGap;
        const calculatedMaxHeight = Math.max(
          200,
          Math.min(availableHeight, maxPossibleHeight)
        );
        setMaxHeight(calculatedMaxHeight);
      } else {
        // Footer not visible, no max height constraint
        setMaxHeight(undefined);
      }
    });

    resizeObserver.observe(formRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isSticky]);

  // Extract experience data - memoized to prevent recreation
  const getExperienceData = React.useCallback(() => {
    if (!experience) return null;

    if (type === 'stay') {
      const stayExp = experience as {
        name?: string;
        isChildAllowed?: boolean;
        pricing?: {
          pricePerPerson?: number;
          currency?: string;
          childPrice?: number;
          groupPricing?: Array<{
            isDisplayPrice?: boolean;
            minHeadCount?: number;
            maxHeadCount?: number;
            adultPrice?: number;
            childPrice?: number;
          }>;
          specialDayPrice?: Array<{
            date: string;
            adultPrice: number;
            childPrice: number;
          }>;
          weekendPrice?: {
            applicableDays?: string[];
            adultPrice?: number;
            childPrice?: number;
          };
          availabilityCalendar?: {
            availableDates?: string[];
            nonAvailableDates?: string[];
            nonAvailableDays?: string[];
            weekendDays?: string[];
            specialDates?: string[];
            defaultAvailability?: boolean;
          };
          isChildAllowed?: boolean;
        };
        termsAndConditions?: string;
        guidelines?: {
          termsAndConditions?: string;
        };
        _id?: string;
      };
      return {
        name: stayExp?.name,
        pricing: stayExp?.pricing,
        isChildAllowed:
          stayExp?.isChildAllowed ?? stayExp?.pricing?.isChildAllowed ?? false,
        termsAndConditions:
          stayExp?.termsAndConditions ||
          stayExp?.guidelines?.termsAndConditions,
        _id: stayExp?._id,
      };
    }

    const exp = experience as {
      price?: number;
      pricePer?: string;
      currency?: string;
      currencySymbol?: string;
      childPrice?: number;
      childPriceUnit?: string;
      useGroupPricing?: boolean;
      groupBooking?: Array<{
        isDisplayPrice?: boolean;
        minHeadCount?: number;
        maxHeadCount?: number;
        adultPrice?: number;
        childPrice?: number;
        targetCurrencyRate?: number;
      }>;
      nonAvailabilityDates?: string[];
      nonAvailableDates?: string[];
      availableDates?: string[];
      defaultAvailability?: boolean;
      nonAvailabilityDays?: string[];
      weekendDays?: string[];
      specialDates?: string[];
      specialDayPricePercentage?: number;
      weekendPricePercentage?: number;
      isChildAllowed?: boolean;
      termsAndConditions?: string;
      guidelines?: {
        termsAndConditions?: string;
      };
      targetCurrencyRate?: number;
      _id?: string;
      // Add duration-related properties
      duration?: { days?: number; nights?: number };
      days?: number | { days?: number };
      nights?: number | { nights?: number };
    };

    return {
      price: exp?.price,
      pricePer: exp?.pricePer,
      currency: exp?.currency || exp?.currencySymbol,
      childPrice: exp?.childPrice,
      useGroupPricing: exp?.useGroupPricing,
      groupBooking: exp?.groupBooking,
      nonAvailabilityDates: exp?.nonAvailabilityDates || [],
      nonAvailableDates: exp?.nonAvailableDates,
      availableDates: exp?.availableDates,
      defaultAvailability: exp?.defaultAvailability,
      nonAvailabilityDays: exp?.nonAvailabilityDays || [],
      weekendDays: exp?.weekendDays || [],
      specialDates: exp?.specialDates || [],
      specialDayPricePercentage: exp?.specialDayPricePercentage,
      weekendPricePercentage: exp?.weekendPricePercentage,
      isChildAllowed: exp?.isChildAllowed,
      termsAndConditions:
        exp?.termsAndConditions || exp?.guidelines?.termsAndConditions,
      targetCurrencyRate: exp?.targetCurrencyRate,
      _id: exp?._id,
      // Add duration-related properties
      duration: exp?.duration,
      days: exp?.days,
      nights: exp?.nights,
    };
  }, [experience, type]);

  // Memoize expData to prevent unnecessary recalculations
  const expData = React.useMemo(() => getExperienceData(), [getExperienceData]);

  const packageCalendar = React.useMemo(() => {
    if (type !== 'package') return null;
    return resolvePackageAvailabilityCalendar({
      defaultAvailability: (
        expData as { defaultAvailability?: boolean } | undefined
      )?.defaultAvailability,
      availableDates: (expData as { availableDates?: string[] } | undefined)
        ?.availableDates,
      nonAvailableDates: (
        expData as { nonAvailableDates?: string[] } | undefined
      )?.nonAvailableDates,
      nonAvailabilityDates: expData?.nonAvailabilityDates,
    });
  }, [type, expData]);

  const usePackageAvailableDatesUI = React.useMemo(
    () => type === 'package' && isPackageAvailableDatesMode(packageCalendar),
    [type, packageCalendar]
  );

  const stayGroupPricingRules = React.useMemo(() => {
    if (type !== 'stay' || !expData?.pricing?.groupPricing?.length) {
      return null;
    }

    const tiers = expData.pricing.groupPricing.filter(
      (
        tier
      ): tier is {
        isDisplayPrice?: boolean;
        minHeadCount: number;
        maxHeadCount: number;
        adultPrice?: number;
        childPrice?: number;
      } =>
        typeof tier.minHeadCount === 'number' &&
        typeof tier.maxHeadCount === 'number'
    );

    if (!tiers.length) return null;

    return {
      minTotalHeadCount: Math.min(...tiers.map(tier => tier.minHeadCount)),
      maxGroupHeadCount: Math.max(...tiers.map(tier => tier.maxHeadCount)),
      tiers,
    };
  }, [type, expData]);

  // Set default adults if there's display group pricing
  useEffect(() => {
    if (!expData) return;
    if (hasBookingIdInUrl && experience && typeof experience === 'object') {
      const booking = (experience as { booking?: unknown }).booking;
      if (booking != null) return;
    }
    const raw = localStorage.getItem('bookingFormDraft');
    if (raw) return;

    if (type === 'stay' && expData.pricing?.groupPricing) {
      const hasDisplayGroup = expData.pricing.groupPricing.find(
        g => g.isDisplayPrice
      );
      if (hasDisplayGroup) {
        setValue('adults', hasDisplayGroup.minHeadCount || 1);
      } else if (stayGroupPricingRules) {
        setValue('adults', stayGroupPricingRules.minTotalHeadCount);
      }
    } else if (type !== 'stay' && expData.groupBooking) {
      const hasDisplayGroup = expData.groupBooking.find(g => g.isDisplayPrice);
      if (hasDisplayGroup) {
        setValue('adults', hasDisplayGroup.minHeadCount || 1);
      }
    }
  }, [expData, type, setValue, hasBookingIdInUrl, experience, stayGroupPricingRules]);

  // Calculate pricing
  const adults = Number(watch('adults')) || 0;
  const children = Number(watch('children')) || 0;
  const totalHeadCount = adults + children;

  // Report traveler count so the page header/bottom bar can reflect group vs standard pricing
  useEffect(() => {
    if (type === 'stay' || type === 'activity' || type === 'package') {
      onTravelerCountChange?.(totalHeadCount);
    }
  }, [type, totalHeadCount, onTravelerCountChange]);

  // Enforce min/max total travelers when stay group pricing is configured
  useEffect(() => {
    if (!stayGroupPricingRules || hasBookingIdInUrl) return;
    if (experience && typeof experience === 'object') {
      const booking = (experience as { booking?: unknown }).booking;
      if (booking != null) return;
    }

    const { minTotalHeadCount, maxGroupHeadCount } = stayGroupPricingRules;
    if (totalHeadCount < minTotalHeadCount) {
      setValue(
        'adults',
        Math.max(adults, minTotalHeadCount - children, 1)
      );
      return;
    }
    if (totalHeadCount > maxGroupHeadCount) {
      const cappedChildren = Math.min(children, maxGroupHeadCount);
      const cappedAdults = Math.max(
        1,
        Math.min(adults, maxGroupHeadCount - cappedChildren)
      );
      if (cappedChildren !== children) {
        setValue('children', cappedChildren);
      }
      if (cappedAdults !== adults) {
        setValue('adults', cappedAdults);
      }
    }
  }, [
    stayGroupPricingRules,
    totalHeadCount,
    adults,
    children,
    setValue,
    hasBookingIdInUrl,
    experience,
  ]);

  const minAdultsForStayGroup =
    stayGroupPricingRules != null
      ? Math.max(1, stayGroupPricingRules.minTotalHeadCount - children)
      : 1;
  const minChildrenForStayGroup =
    stayGroupPricingRules != null
      ? Math.max(0, stayGroupPricingRules.minTotalHeadCount - adults)
      : 0;
  const maxAdultsForStayGroup =
    stayGroupPricingRules != null
      ? Math.max(
          minAdultsForStayGroup,
          stayGroupPricingRules.maxGroupHeadCount - children
        )
      : undefined;
  const maxChildrenForStayGroup =
    stayGroupPricingRules != null
      ? Math.max(
          minChildrenForStayGroup,
          stayGroupPricingRules.maxGroupHeadCount - adults
        )
      : undefined;
  const selectedDate = watch('date');
  const startDate = watch('startDate');
  const endDate = watch('endDate');

  // Clear invalid or past start/end dates in package dropdown flow
  React.useEffect(() => {
    if (!usePackageAvailableDatesUI) return;
    if (startDate && !isValidPackageDate(startDate)) {
      setValue('startDate', undefined);
      setValue('endDate', undefined);
      return;
    }
    if (!startDate) return;
    if (isPastPackageDate(startDate, startOfLocalDay())) {
      setValue('startDate', undefined);
      setValue('endDate', undefined);
    }
  }, [usePackageAvailableDatesUI, startDate, setValue]);

  let adultPrice = 0;
  let childPrice = 0;
  let totalCost = 0;
  let appliedPricingType: 'standard' | 'group' | 'special' | 'weekend' =
    'standard';

  if (type === 'stay' && expData?.pricing) {
    adultPrice = expData.pricing.pricePerPerson ?? 0;
    childPrice = expData.pricing.childPrice ?? 0;

    // Check group pricing
    const matchedGroup = expData.pricing.groupPricing?.find(
      g =>
        typeof g.minHeadCount === 'number' &&
        typeof g.maxHeadCount === 'number' &&
        totalHeadCount >= g.minHeadCount &&
        totalHeadCount <= g.maxHeadCount
    );
    if (matchedGroup) {
      adultPrice = matchedGroup.adultPrice ?? adultPrice;
      childPrice = matchedGroup.childPrice ?? childPrice;
      appliedPricingType = 'group';
    }

    // Check special/weekend pricing for start date
    if (startDate) {
      const formattedDate = startDate.toISOString().split('T')[0];
      const specialDay = expData.pricing.specialDayPrice?.find(
        s => s.date === formattedDate
      );
      if (specialDay) {
        adultPrice = specialDay.adultPrice;
        childPrice = specialDay.childPrice;
        appliedPricingType = 'special';
      } else {
        const dayName = startDate
          .toLocaleDateString('en-US', { weekday: 'long' })
          .toLowerCase();
        if (expData.pricing.weekendPrice?.applicableDays?.includes(dayName)) {
          adultPrice = expData.pricing.weekendPrice.adultPrice ?? adultPrice;
          childPrice = expData.pricing.weekendPrice.childPrice ?? childPrice;
          appliedPricingType = 'weekend';
        }
      }
    }

    // Calculate nights
    const nights =
      startDate && endDate
        ? Math.ceil(
            (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
          )
        : 1;
    totalCost = (adults * adultPrice + children * childPrice) * nights;
  } else if (expData) {
    // Activity or Package
    const baseAdultPrice = expData.price ?? 0;
    const baseChildPrice = expData.childPrice ?? 0;
    const isCouplePricing = expData.pricePer?.toLowerCase()?.includes('couple');

    // Check group pricing
    const groupBooking = expData.groupBooking?.find(
      group =>
        typeof group.minHeadCount === 'number' &&
        typeof group.maxHeadCount === 'number' &&
        totalHeadCount >= group.minHeadCount &&
        totalHeadCount <= group.maxHeadCount
    );

    if (groupBooking) {
      adultPrice = groupBooking.adultPrice ?? baseAdultPrice;
      childPrice = groupBooking.childPrice ?? baseChildPrice;
      appliedPricingType = 'group';
    } else {
      adultPrice = baseAdultPrice;
      childPrice = baseChildPrice;
    }

    // Handle couple pricing
    if (isCouplePricing && !groupBooking) {
      const perPersonRate = baseAdultPrice / 2;
      totalCost =
        adults * perPersonRate + children * (baseChildPrice || perPersonRate);
    } else {
      totalCost = adults * adultPrice + children * childPrice;
    }

    // Apply special/weekend percentage
    if (dateStatus === 'special' && expData.specialDayPricePercentage) {
      totalCost += (totalCost * expData.specialDayPricePercentage) / 100;
      appliedPricingType = 'special';
    } else if (dateStatus === 'weekend' && expData.weekendPricePercentage) {
      totalCost += (totalCost * expData.weekendPricePercentage) / 100;
      appliedPricingType = 'weekend';
    }
  }

  // UX Fix: Calculate display total based on rounded unit prices to avoid discrepancies like 1+1=3
  // Mathematically correct total remains totalCost (for backend), but we show a consistent sum to user
  const backendCurrency =
    type === 'stay' ? expData?.pricing?.currency : expData?.currency;
  const backendRate =
    type === 'stay'
      ? undefined
      : (expData?.groupBooking?.find(
          g =>
            totalHeadCount >= g.minHeadCount && totalHeadCount <= g.maxHeadCount
        )?.targetCurrencyRate ?? expData?.targetCurrencyRate);

  const calculateDisplayAmount = (baseUsd: number) => {
    // If it's a package/activity, we might need to apply percentages to the baseUSD first
    let adjustedUsd = baseUsd;
    if (type !== 'stay') {
      if (dateStatus === 'special' && expData.specialDayPricePercentage) {
        adjustedUsd += (adjustedUsd * expData.specialDayPricePercentage) / 100;
      } else if (dateStatus === 'weekend' && expData.weekendPricePercentage) {
        adjustedUsd += (adjustedUsd * expData.weekendPricePercentage) / 100;
      }
    }
    const converted = convertPrice(adjustedUsd, backendCurrency, backendRate);
    return Math.round(converted);
  };

  const bookingPriceDetails =
    hasBookingIdInUrl && experience && typeof experience === 'object'
      ? (
          experience as {
            booking?: {
              priceDetails?: {
                totalCost?: number;
                currency?: string;
                currencySymbol?: string;
                pricePerAdult?: number;
                pricePerChild?: number;
              };
            };
          }
        ).booking?.priceDetails
      : undefined;

  const useBookingSnapshotUnitPrices =
    hasBookingIdInUrl &&
    bookingPriceDetails != null &&
    typeof bookingPriceDetails.pricePerAdult === 'number';

  const bookingTotalFromApi =
    bookingPriceDetails != null &&
    typeof bookingPriceDetails.totalCost === 'number';

  const snapshotPriceCurrency =
    typeof bookingPriceDetails?.currency === 'string' &&
    bookingPriceDetails.currency.trim() !== ''
      ? bookingPriceDetails.currency
      : (type === 'stay' ? expData?.pricing?.currency : expData?.currency) ||
        userCurrencyCode;

  const displayAdultPrice = useBookingSnapshotUnitPrices
    ? (bookingPriceDetails?.pricePerAdult ?? 0)
    : calculateDisplayAmount(
        type === 'stay' ||
          !expData?.pricePer?.toLowerCase()?.includes('couple') ||
          expData?.groupBooking?.find(
            g =>
              totalHeadCount >= g.minHeadCount &&
              totalHeadCount <= g.maxHeadCount
          )
          ? adultPrice
          : adultPrice / 2
      );
  const displayChildPrice = useBookingSnapshotUnitPrices
    ? typeof bookingPriceDetails?.pricePerChild === 'number'
      ? bookingPriceDetails.pricePerChild
      : calculateDisplayAmount(childPrice)
    : calculateDisplayAmount(childPrice);

  const displayTotal = bookingTotalFromApi
    ? (bookingPriceDetails?.totalCost ?? 0)
    : type === 'stay'
      ? (displayAdultPrice * adults + displayChildPrice * children) *
        (startDate && endDate
          ? Math.ceil(
              (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
            )
          : 1)
      : displayAdultPrice * adults + displayChildPrice * children;

  const unitPriceBackendCurrency = useBookingSnapshotUnitPrices
    ? snapshotPriceCurrency
    : userCurrencyCode;
  const totalCostBackendCurrency = bookingTotalFromApi
    ? snapshotPriceCurrency
    : userCurrencyCode;

  // Get availability data - memoized to prevent infinite re-renders
  // Use refs to track previous values and only update when content actually changes
  const prevNonAvailableDatesRef = React.useRef<string[]>([]);
  const prevNonAvailableDaysRef = React.useRef<string[]>([]);
  const prevWeekendDaysRef = React.useRef<string[]>([]);
  const prevSpecialDatesRef = React.useRef<string[]>([]);

  const NON_AVAILABILITY_DATES = React.useMemo(() => {
    const dates =
      type === 'package' && packageCalendar
        ? packageCalendar.nonAvailableDates
        : type === 'stay'
          ? expData?.pricing?.availabilityCalendar?.nonAvailableDates
          : expData?.nonAvailabilityDates;
    const currentDates = dates && Array.isArray(dates) ? dates : [];

    // Compare arrays by content, not reference
    const isSame =
      currentDates.length === prevNonAvailableDatesRef.current.length &&
      currentDates.every((d, i) => d === prevNonAvailableDatesRef.current[i]);

    if (isSame) {
      return prevNonAvailableDatesRef.current;
    }

    prevNonAvailableDatesRef.current = currentDates;
    return currentDates;
  }, [
    type,
    packageCalendar,
    expData?.pricing?.availabilityCalendar?.nonAvailableDates,
    expData?.nonAvailabilityDates,
  ]);

  const CALENDAR_DEFAULT_AVAILABILITY = React.useMemo(() => {
    if (type === 'package' && packageCalendar) {
      return packageCalendar.defaultAvailability;
    }
    if (type === 'stay') {
      return (
        expData?.pricing?.availabilityCalendar?.defaultAvailability ?? true
      );
    }
    return true;
  }, [
    type,
    packageCalendar,
    expData?.pricing?.availabilityCalendar?.defaultAvailability,
  ]);

  const CALENDAR_AVAILABLE_DATES = React.useMemo(() => {
    if (type === 'package' && packageCalendar) {
      return packageCalendar.availableDates;
    }
    if (type === 'stay') {
      return expData?.pricing?.availabilityCalendar?.availableDates ?? [];
    }
    return [];
  }, [
    type,
    packageCalendar,
    expData?.pricing?.availabilityCalendar?.availableDates,
  ]);

  const NON_AVAILABILITY_DAYS = React.useMemo(() => {
    const days =
      type === 'stay'
        ? expData?.pricing?.availabilityCalendar?.nonAvailableDays
        : expData?.nonAvailabilityDays;
    const currentDays = days && Array.isArray(days) ? days : [];

    const isSame =
      currentDays.length === prevNonAvailableDaysRef.current.length &&
      currentDays.every((d, i) => d === prevNonAvailableDaysRef.current[i]);

    if (isSame) {
      return prevNonAvailableDaysRef.current;
    }

    prevNonAvailableDaysRef.current = currentDays;
    return currentDays;
  }, [
    type,
    expData?.pricing?.availabilityCalendar?.nonAvailableDays,
    expData?.nonAvailabilityDays,
  ]);

  const WEEKEND_DAYS = React.useMemo(() => {
    const days =
      type === 'stay'
        ? expData?.pricing?.availabilityCalendar?.weekendDays
        : expData?.weekendDays;
    const currentDays = days && Array.isArray(days) ? days : [];

    const isSame =
      currentDays.length === prevWeekendDaysRef.current.length &&
      currentDays.every((d, i) => d === prevWeekendDaysRef.current[i]);

    if (isSame) {
      return prevWeekendDaysRef.current;
    }

    prevWeekendDaysRef.current = currentDays;
    return currentDays;
  }, [
    type,
    expData?.pricing?.availabilityCalendar?.weekendDays,
    expData?.weekendDays,
  ]);

  const SPECIAL_DATES = React.useMemo(() => {
    const dates =
      type === 'stay'
        ? expData?.pricing?.availabilityCalendar?.specialDates
        : expData?.specialDates;
    const currentDates = dates && Array.isArray(dates) ? dates : [];

    const isSame =
      currentDates.length === prevSpecialDatesRef.current.length &&
      currentDates.every((d, i) => d === prevSpecialDatesRef.current[i]);

    if (isSame) {
      return prevSpecialDatesRef.current;
    }

    prevSpecialDatesRef.current = currentDates;
    return currentDates;
  }, [
    type,
    expData?.pricing?.availabilityCalendar?.specialDates,
    expData?.specialDates,
  ]);

  // Memoized date select handlers to prevent infinite re-renders
  const handleStartDateSelect = React.useCallback(
    (date: Date, status?: 'special' | 'weekend' | 'allowed') => {
      console.log('🗓️ Start date selected:', date);
      console.log('📦 Type:', type);
      console.log('📦 Experience data:', expData);
      console.log('📦 Experience data keys:', Object.keys(expData || {}));
      console.log(
        '📦 Duration:',
        (expData as { duration?: unknown })?.duration
      );
      console.log('📦 Days:', (expData as { days?: unknown })?.days);
      console.log('📦 Nights:', (expData as { nights?: unknown })?.nights);
      console.log('📦 Details:', (expData as { details?: unknown })?.details);

      setValue('startDate', date);
      clearErrors('startDate');

      // Auto-calculate end date for packages based on duration
      if (type === 'package' && expData) {
        console.log('✅ Package detected, checking for duration...');

        let days = 1;
        let hasDuration = false;

        // Use the same logic as ExperienceHeader for extracting days
        const exp = expData as {
          duration?: { days?: number; nights?: number };
          days?: number | { days?: number };
          nights?: number | { nights?: number };
        };

        if (exp?.duration && typeof exp.duration === 'object') {
          days = exp.duration.days || exp.duration.nights || 1;
          hasDuration = !!(exp.duration.days || exp.duration.nights);
          console.log('📅 Duration from duration object:', exp.duration);
        } else if (typeof exp?.days === 'number') {
          days = exp.days;
          hasDuration = true;
          console.log('📅 Days from days property:', exp.days);
        } else if (exp?.days && typeof exp.days === 'object') {
          days = (exp.days as { days?: number }).days || 1;
          hasDuration = !!(exp.days as { days?: number }).days;
          console.log('📅 Days from days object:', exp.days);
        } else if (typeof exp?.nights === 'number') {
          days = exp.nights;
          hasDuration = true;
          console.log('📅 Days from nights property:', exp.nights);
        } else if (exp?.nights && typeof exp.nights === 'object') {
          days = (exp.nights as { nights?: number }).nights || 1;
          hasDuration = !!(exp.nights as { nights?: number }).nights;
          console.log('📅 Days from nights object:', exp.nights);
        }

        console.log('📅 Has duration:', hasDuration);
        console.log('📅 Final days calculated:', days);

        // Only auto-calculate end date if we have valid duration information and days > 1
        if (hasDuration && days > 1) {
          // Calculate end date: start date + (days - 1) days (since start date counts as day 1)
          const endDate = new Date(date);
          endDate.setDate(endDate.getDate() + (days - 1));

          console.log('📅 Calculated end date:', endDate);

          setValue('endDate', endDate);
          clearErrors('endDate');
          console.log('✅ End date set successfully');
        } else {
          console.log(
            '❌ No valid duration found or duration is 1 day, not setting end date'
          );
        }
      }

      // Stays default to a 1-night stay (checkout = check-in + 1 day)
      if (type === 'stay') {
        const checkoutDate = new Date(date);
        checkoutDate.setDate(checkoutDate.getDate() + 1);
        setValue('endDate', checkoutDate);
        clearErrors('endDate');
      } else if (endDate && date && endDate <= date && type !== 'package') {
        // If endDate is before or equal to new startDate, clear it (for non-packages)
        setValue('endDate', undefined);
        clearErrors('endDate');
      }

      // For packages, also update dateStatus if status is provided
      if (type === 'package' && status) {
        setDateStatus(status);
      }
    },
    [endDate, setValue, clearErrors, type, expData]
  );

  const handleEndDateSelect = React.useCallback(
    (date: Date, status?: 'special' | 'weekend' | 'allowed') => {
      // Validate that endDate is after startDate
      if (startDate && date <= startDate) {
        setError('endDate', {
          type: 'manual',
          message:
            type === 'stay'
              ? 'Check-out date must be after check-in date'
              : 'End date must be after start date',
        });
        return;
      }
      setValue('endDate', date);
      clearErrors('endDate');
      // Update dateStatus for packages if status is provided
      if (type === 'package' && status) {
        setDateStatus(status);
      }
    },
    [startDate, setValue, setError, clearErrors, type]
  );

  const handleDateSelect = React.useCallback(
    (date: Date, status: 'special' | 'weekend' | 'allowed') => {
      setDateStatus(status);
      setValue('date', date);
      clearErrors('date');
    },
    [setValue, clearErrors]
  );

  // Default to the nearest available booking date on detail page entry
  React.useEffect(() => {
    const hasDateAlready =
      type === 'activity' ? Boolean(selectedDate) : Boolean(startDate);
    if (hasDateAlready) return;

    if (hasBookingIdInUrl && experience && typeof experience === 'object') {
      const booking = (experience as { booking?: unknown }).booking;
      if (booking != null) return;
    }
    if (!restore && localStorage.getItem('bookingFormDraft')) return;

    const nearest = getNearestAvailableBookingDate({
      defaultAvailability: CALENDAR_DEFAULT_AVAILABILITY,
      availableDates: CALENDAR_AVAILABLE_DATES,
      nonAvailableDates: NON_AVAILABILITY_DATES,
      nonAvailabilityDays: NON_AVAILABILITY_DAYS,
    });
    if (!nearest) return;

    const status = getPackageDatePricingStatus(
      nearest,
      WEEKEND_DAYS,
      SPECIAL_DATES
    );

    if (type === 'activity') {
      handleDateSelect(nearest, status);
    } else {
      handleStartDateSelect(nearest, status);
    }
  }, [
    type,
    selectedDate,
    startDate,
    hasBookingIdInUrl,
    experience,
    restore,
    CALENDAR_DEFAULT_AVAILABILITY,
    CALENDAR_AVAILABLE_DATES,
    NON_AVAILABILITY_DATES,
    NON_AVAILABILITY_DAYS,
    WEEKEND_DAYS,
    SPECIAL_DATES,
    handleDateSelect,
    handleStartDateSelect,
  ]);

  // Initial validation handler
  const handleInitialValidation = () => {
    let hasError = false;

    const date = type === 'activity' ? selectedDate : null;
    const adultsValue = watch('adults');

    if (type === 'stay' || type === 'package') {
      if (!startDate) {
        setError('startDate', {
          type: 'manual',
          message:
            type === 'stay'
              ? 'Please select a check-in date'
              : 'Please select a start date',
        });
        hasError = true;
      }
      if (!endDate) {
        setError('endDate', {
          type: 'manual',
          message:
            type === 'stay'
              ? 'Please select a check-out date'
              : 'Please select an end date',
        });
        hasError = true;
      }
      if (startDate && endDate && endDate <= startDate) {
        setError('endDate', {
          type: 'manual',
          message:
            type === 'stay'
              ? 'Check-out date must be after check-in date'
              : 'End date must be after start date',
        });
        hasError = true;
      }
    } else {
      if (!date) {
        setError('date', {
          type: 'manual',
          message: 'Please select a date',
        });
        hasError = true;
      }
    }
    if (!adultsValue || adultsValue < 1) {
      setError('adults', {
        type: 'manual',
        message: 'At least 1 adult is required',
      });
      hasError = true;
    }

    const childrenValue = Number(watch('children')) || 0;
    const travellersCount = (adultsValue || 0) + childrenValue;
    if (
      type === 'stay' &&
      stayGroupPricingRules &&
      travellersCount < stayGroupPricingRules.minTotalHeadCount
    ) {
      setError('adults', {
        type: 'manual',
        message: `Minimum ${stayGroupPricingRules.minTotalHeadCount} travelers required`,
      });
      hasError = true;
    }
    if (
      type === 'stay' &&
      stayGroupPricingRules &&
      travellersCount > stayGroupPricingRules.maxGroupHeadCount
    ) {
      setError('adults', {
        type: 'manual',
        message: `Maximum ${stayGroupPricingRules.maxGroupHeadCount} travelers allowed`,
      });
      hasError = true;
    }

    // Guests can enter contact details without SSO login
    if (!hasError) {
      setShowUserDetails(true);
    }
  };

  // Final submit handler
  const onSubmit = async (data: BookingFormValues) => {
    if (!expData?._id) {
      console.error('Experience ID not found');
      return;
    }

    // Log initial state for debugging
    console.log('[FloatingBookingForm] onSubmit called', {
      type,
      experienceId: expData._id,
      watchValues: {
        date: selectedDate,
        startDate: startDate,
        endDate: endDate,
      },
      formData: {
        date: data.date,
        startDate: data.startDate,
        endDate: data.endDate,
      },
    });

    // Use watch values as primary source since they're used throughout the component
    // Fall back to data values if watch doesn't have them
    const selectedStartDate = startDate || data.startDate;
    const selectedEndDate = endDate || data.endDate;
    const selectedDateValue = selectedDate || data.date;

    console.log('[FloatingBookingForm] After fallback logic', {
      selectedStartDate,
      selectedEndDate,
      selectedDateValue,
      startDateIsDate: selectedStartDate instanceof Date,
      endDateIsDate: selectedEndDate instanceof Date,
      dateValueIsDate: selectedDateValue instanceof Date,
    });

    // For packages, if we have startDate but not endDate, use startDate for both
    // This is a defensive measure in case validation is bypassed
    const packageStartDate =
      type === 'package' && selectedStartDate && !selectedEndDate
        ? selectedStartDate
        : selectedStartDate;
    const packageEndDate =
      type === 'package' && selectedStartDate && !selectedEndDate
        ? selectedStartDate
        : selectedEndDate;

    if (type === 'package') {
      console.log('[FloatingBookingForm] Package date handling', {
        hasStartDate: !!selectedStartDate,
        hasEndDate: !!selectedEndDate,
        startDateIsDate: selectedStartDate instanceof Date,
        endDateIsDate: selectedEndDate instanceof Date,
        usingFallback:
          type === 'package' && selectedStartDate && !selectedEndDate,
        packageStartDate,
        packageEndDate,
        packageStartDateIsDate: packageStartDate instanceof Date,
        packageEndDateIsDate: packageEndDate instanceof Date,
      });
    }

    // Calculate numberOfNights for stay experiences
    const numberOfNights =
      type === 'stay' &&
      selectedStartDate &&
      selectedEndDate &&
      selectedStartDate instanceof Date &&
      selectedEndDate instanceof Date
        ? Math.ceil(
            (selectedEndDate.getTime() - selectedStartDate.getTime()) /
              (1000 * 60 * 60 * 24)
          )
        : undefined;

    const profileName = name && name !== 'User Name' ? name : '';
    const profileEmail =
      email && email !== 'user@example.com' ? email : '';

    const payload = {
      user: {
        userName: data.userName || profileName,
        userEmail: data.userEmail || profileEmail,
        userMobile: (data.countryCode || '+91') + (data.phoneDigits || ''),
      },
      ...(type === 'activity'
        ? { guidedActivity: expData._id }
        : type === 'package'
          ? { package: expData._id }
          : {}), // For stay, don't include guidedActivity - backend validates it before checking stayBooking
      numberOfChildren: children,
      numberOfAdults: adults,
      bookingDate:
        type === 'stay' || type === 'package'
          ? selectedStartDate instanceof Date
            ? selectedStartDate.toISOString()
            : undefined
          : selectedDateValue instanceof Date
            ? selectedDateValue.toISOString()
            : undefined,
      // Always include bookingStartDate and bookingEndDate for package bookings
      ...(type === 'package' &&
        packageStartDate &&
        packageEndDate &&
        packageStartDate instanceof Date &&
        packageEndDate instanceof Date && {
          bookingStartDate: packageStartDate.toISOString(),
          bookingEndDate: packageEndDate.toISOString(),
        }),
      ...(type === 'stay' &&
        selectedStartDate &&
        selectedEndDate &&
        selectedStartDate instanceof Date &&
        selectedEndDate instanceof Date &&
        numberOfNights &&
        expData.name && {
          guidedActivity: expData._id, // Include guidedActivity for stay (backend expects this)
          stayBooking: {
            stayTitle: expData.name,
            checkIn: selectedStartDate.toISOString(),
            checkOut: selectedEndDate.toISOString(),
            numberOfNights: numberOfNights,
            currency: currencyCode,
          },
        }),
      bookingStatus: 'Requested',
      comments: 'Booking from form',
      bookingType: 'direct',
      paymentStatus: 'Pending',
      priceDetails: {
        pricePerAdult: displayAdultPrice,
        pricePerChild: displayChildPrice,
        totalCost: displayTotal,
        currency: currencyCode,
        currencySymbol: currencySymbol,
        ...(type === 'stay' && { appliedPricingType }),
      },
    };

    // Log final payload before sending
    const hasBookingStartDate = 'bookingStartDate' in payload;
    const hasBookingEndDate = 'bookingEndDate' in payload;

    console.log('[FloatingBookingForm] Final payload check', {
      type,
      hasBookingStartDate,
      hasBookingEndDate,
      rawBaseTotalCost: totalCost,
      convertedDisplayTotal: displayTotal,
      bookingStartDate: payload.bookingStartDate,
      bookingEndDate: payload.bookingEndDate,
      bookingDate: payload.bookingDate,
      packageConditionMet:
        type === 'package' &&
        packageStartDate &&
        packageEndDate &&
        packageStartDate instanceof Date &&
        packageEndDate instanceof Date,
    });

    if (type === 'package' && (!hasBookingStartDate || !hasBookingEndDate)) {
      console.error('[FloatingBookingForm] ⚠️ MISSING DATES FOR PACKAGE!', {
        hasBookingStartDate,
        hasBookingEndDate,
        packageStartDate,
        packageEndDate,
        packageStartDateType: typeof packageStartDate,
        packageEndDateType: typeof packageEndDate,
        packageStartDateIsDate: packageStartDate instanceof Date,
        packageEndDateIsDate: packageEndDate instanceof Date,
        payload: JSON.stringify(payload, null, 2),
      });
    }

    const result = await addBooking(payload);
    if (result) {
      const fullMobile = (data.countryCode || '+91') + (data.phoneDigits || '');
      setMobile(fullMobile);
      setToastConfig({
        message:
          'Thank you! Your enquiry has been sent. Our customer executive will contact you shortly.',
        type: 'success',
      });
      setTimeout(() => {
        setShowToast(true);
      }, 400);
      setShowUserDetails(false);
      reset({
        date: undefined,
        startDate: undefined,
        endDate: undefined,
        adults: 1,
        children: 0,
        userName: name && name !== 'User Name' ? name : '',
        userEmail: email && email !== 'user@example.com' ? email : '',
        userMobile: '',
        countryCode: '+91',
        phoneDigits: '',
        acceptTerms: false,
      });
    } else {
      console.error('Failed to add booking:', result, payload);
      setToastConfig({
        message:
          'Something went wrong. Please try again later or contact support.',
        type: 'error',
      });
      setShowToast(true);
    }
  };

  const termsAndConditions = expData?.termsAndConditions;

  return (
    <>
      <div
        ref={formRef}
        className={`bg-white rounded-lg shadow-lg p-4 md:p-6 border border-gray-200 w-full lg:max-w-[338px] ${
          isSticky ? 'lg:fixed' : 'relative'
        }`}
        style={{
          top:
            isSticky && bottomOffset === undefined ? `${topOffset}px` : 'auto',
          bottom:
            isSticky && bottomOffset !== undefined
              ? `${bottomOffset + 20}px` // Add 20px gap when stuck to bottom
              : 'auto',
          ...(isSticky && formRef.current
            ? { width: `${formRef.current.offsetWidth}px` }
            : {}),
          zIndex: isSticky ? 50 : 1,
        }}
      >
        <div
          className='booking-form-scroll overflow-y-auto'
          style={{
            // Apply max-height to inner div to allow scrolling
            maxHeight:
              isSticky && maxHeight !== undefined
                ? `${maxHeight - 48}px` // Subtract padding (24px top + 24px bottom)
                : 'calc(100vh - 8rem)',
          }}
        >
          <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
            {/* Date Selection */}
            <div>
              {type === 'activity' && (
                <SubTitle
                  title='Select Date *'
                  className='mb-2'
                  fontSize={typography.size.base}
                />
              )}
              {type === 'stay' || type === 'package' ? (
                <div className='space-y-3'>
                  <div>
                    <SubTitle
                      title={
                        type === 'stay' ? 'Check-in Date *' : 'Start Date *'
                      }
                      className='mb-1.5'
                      fontSize={typography.size.base}
                    />
                    {usePackageAvailableDatesUI ? (
                      <DropdownDatePicker
                        availableDates={CALENDAR_AVAILABLE_DATES}
                        value={startDate}
                        weekendDays={WEEKEND_DAYS}
                        specialDates={SPECIAL_DATES}
                        disabled={hasBookingIdInUrl}
                        inBottomSheet={inBottomSheet}
                        onDateSelect={(date, status) => {
                          handleStartDateSelect(date, status);
                        }}
                      />
                    ) : (
                      <DatePicker
                        className='w-full bg-white'
                        value={startDate}
                        nonAvailabilityDates={NON_AVAILABILITY_DATES}
                        nonAvailabilityDays={NON_AVAILABILITY_DAYS}
                        weekendDays={WEEKEND_DAYS}
                        specialDates={SPECIAL_DATES}
                        defaultAvailability={CALENDAR_DEFAULT_AVAILABILITY}
                        availableDates={CALENDAR_AVAILABLE_DATES}
                        disabled={hasBookingIdInUrl}
                        onDateSelect={(date, status) => {
                          if (type === 'package' || type === 'stay') {
                            handleStartDateSelect(date, status);
                          } else {
                            handleDateSelect(date, status);
                          }
                        }}
                      />
                    )}
                    {errors.startDate && (
                      <p className='text-red-500 text-xs mt-1'>
                        {errors.startDate.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <SubTitle
                      title={
                        type === 'stay' ? 'Check-out Date *' : 'End Date *'
                      }
                      fontSize={typography.size.base}
                      className='mb-1.5'
                    />
                    {usePackageAvailableDatesUI ? (
                      <ReadOnlyDateDisplay value={endDate} />
                    ) : (
                      <DatePicker
                        className='w-full bg-white'
                        value={endDate}
                        nonAvailabilityDates={NON_AVAILABILITY_DATES}
                        nonAvailabilityDays={NON_AVAILABILITY_DAYS}
                        weekendDays={WEEKEND_DAYS}
                        specialDates={SPECIAL_DATES}
                        defaultAvailability={CALENDAR_DEFAULT_AVAILABILITY}
                        availableDates={CALENDAR_AVAILABLE_DATES}
                        disabled={hasBookingIdInUrl}
                        onDateSelect={(date, status) => {
                          handleEndDateSelect(date, status);
                        }}
                      />
                    )}
                    {errors.endDate && (
                      <p className='text-red-500 text-xs mt-1'>
                        {errors.endDate.message}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <DatePicker
                    className='w-full bg-white'
                    value={selectedDate}
                    nonAvailabilityDates={NON_AVAILABILITY_DATES}
                    nonAvailabilityDays={NON_AVAILABILITY_DAYS}
                    weekendDays={WEEKEND_DAYS}
                    specialDates={SPECIAL_DATES}
                    defaultAvailability={CALENDAR_DEFAULT_AVAILABILITY}
                    availableDates={CALENDAR_AVAILABLE_DATES}
                    disabled={hasBookingIdInUrl}
                    onDateSelect={handleDateSelect}
                  />
                  {errors.date && (
                    <p className='text-red-500 text-xs mt-1'>
                      {errors.date.message}
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Travellers Selection */}
            <div>
              <SubTitle
                title='Select Travellers *'
                className='mb-2'
                fontSize={typography.size.base}
              />
              <CounterButton
                value={adults}
                min={minAdultsForStayGroup}
                max={maxAdultsForStayGroup}
                disabled={hasBookingIdInUrl}
                onChange={newValue => {
                  const clampedValue = Math.min(
                    maxAdultsForStayGroup ?? newValue,
                    Math.max(minAdultsForStayGroup, newValue)
                  );
                  setValue('adults', clampedValue);
                  if (clampedValue >= 1) {
                    clearErrors('adults');
                  } else {
                    setError('adults', {
                      type: 'manual',
                      message: 'At least 1 adult is required',
                    });
                  }
                }}
                label='Adults'
                description='Aged 9+'
              />
              {errors.adults && (
                <p
                  className='text-red-500 mt-1'
                  style={{
                    fontSize: typography.size.xs, // 12px
                    fontFamily: typography.fonts.primary,
                  }}
                >
                  {errors.adults.message}
                </p>
              )}

              {expData?.isChildAllowed && (
                <CounterButton
                  value={children}
                  min={minChildrenForStayGroup}
                  max={maxChildrenForStayGroup}
                  disabled={hasBookingIdInUrl}
                  onChange={newValue => {
                    setValue(
                      'children',
                      Math.min(
                        maxChildrenForStayGroup ?? newValue,
                        Math.max(minChildrenForStayGroup, newValue)
                      )
                    );
                  }}
                  label='Children'
                  className='mt-5'
                  description='Aged 0-8'
                />
              )}
            </div>

            {/* Total Cost */}
            <Border />
            <div className='flex justify-between items-center pb-1.5 pt-3'>
              <span
                className='font-semibold text-gray-900'
                style={{
                  fontSize: typography.size.lg, // 18px
                  fontFamily: typography.fonts.primary,
                }}
              >
                Total Cost
              </span>
              <span
                className='font-bold text-gray-900'
                style={{
                  fontSize: '32px',
                  fontFamily: typography.fonts.primary,
                }}
              >
                {/* Booking snapshot: amounts from booking.priceDetails use API currency; otherwise user-converted totals */}
                <CurrencyDisplay
                  amount={displayTotal}
                  backendCurrency={totalCostBackendCurrency}
                  showDecimals={false}
                  fontSize='32px'
                />
              </span>
            </div>

            {/* Price Breakdown */}
            <div className='text-xs text-gray-600 mt-2 space-y-1'>
              <div className='flex justify-between items-center'>
                <span>Price per Person:</span>
                <span className='font-medium'>
                  <CurrencyDisplay
                    amount={displayAdultPrice}
                    backendCurrency={unitPriceBackendCurrency}
                    showDecimals={false}
                  />
                  {type === 'stay' ? '/night' : ''}
                </span>
              </div>
              {expData?.isChildAllowed && (
                <div className='flex justify-between items-center'>
                  <span>Price per Child:</span>
                  <span className='font-medium'>
                    <CurrencyDisplay
                      amount={displayChildPrice}
                      backendCurrency={unitPriceBackendCurrency}
                      showDecimals={false}
                    />
                    {type === 'stay' ? '/night' : ''}
                  </span>
                </div>
              )}
              <div className='flex justify-between items-center'>
                <span>Number of People:</span>
                <span className='font-medium'>
                  {totalHeadCount} {totalHeadCount === 1 ? 'person' : 'people'}
                  {adults > 0 && children > 0 && (
                    <span className='text-gray-500 ml-1'>
                      ({adults} {adults === 1 ? 'adult' : 'adults'}
                      {children > 0 &&
                        `, ${children} ${children === 1 ? 'child' : 'children'}`}
                      )
                    </span>
                  )}
                </span>
              </div>
            </div>

            {/* Pricing Info */}
            <div className='text-xs mt-2 text-gray-900 flex items-start gap-1'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                viewBox='0 0 20 20'
                fill='currentColor'
                className='w-3.5 h-3.5 flex-shrink-0 mt-0.5'
              >
                <path
                  fillRule='evenodd'
                  d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z'
                  clipRule='evenodd'
                />
              </svg>
              <span>
                {appliedPricingType === 'group' ? (
                  <>
                    Group pricing applied: {totalHeadCount} people at{' '}
                    <CurrencyDisplay
                      amount={displayAdultPrice}
                      backendCurrency={unitPriceBackendCurrency}
                      showDecimals={false}
                    />
                    /person{type === 'stay' ? '/night' : ''}
                  </>
                ) : appliedPricingType === 'special' ? (
                  'Special day pricing applied'
                ) : appliedPricingType === 'weekend' ? (
                  'Weekend pricing applied'
                ) : stayGroupPricingRules &&
                  totalHeadCount > stayGroupPricingRules.maxGroupHeadCount ? (
                  `Standard pricing applied (group rate available for up to ${stayGroupPricingRules.maxGroupHeadCount} travelers)`
                ) : (
                  'Individual pricing applied'
                )}
              </span>
            </div>

            {/* Terms and Conditions - when booking ID in URL, show link outside User Details so it's always visible */}
            {termsAndConditions && hasBookingIdInUrl && (
              <div className='mt-4'>
                <Border />
                <div className='text-sm text-gray-700 pt-3'>
                  <Dialog>
                    <DialogTrigger asChild>
                      <button
                        type='button'
                        className='text-blue-600 underline hover:text-blue-700'
                      >
                        Terms and Conditions
                      </button>
                    </DialogTrigger>
                    <DialogContent className='sm:!max-w-3xl md:!max-w-4xl lg:!max-w-5xl'>
                      <DialogHeader>
                        <DialogTitle className='text-2xl font-semibold text-black'>
                          Terms and Conditions
                        </DialogTitle>
                      </DialogHeader>
                      <div className='h-px bg-gray-200 -mx-6 mb-2' />
                      <div className='max-h-[70vh] overflow-auto text-sm text-gray-800 leading-6'>
                        <TermsAndConditionsContent html={termsAndConditions} />
                      </div>
                      <div className='mt-4 flex items-center justify-end'>
                        <DialogClose asChild>
                          <button
                            type='button'
                            className='btn btn-secondary px-4 py-2'
                          >
                            Close
                          </button>
                        </DialogClose>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            )}

            {/* User Details Section */}
            {showUserDetails && (
              <div ref={userDetailsRef} className='space-y-4'>
                <Border />
                <SubTitle title='User Details *' className='mb-2 mt-2' />
                <div className='space-y-4'>
                  <CustomInput
                    name='userName'
                    placeholder='Enter your full name'
                    type='text'
                    defaultValue={name === 'User Name' ? '' : name}
                    register={register}
                    rules={{
                      required: 'Please enter a valid name',
                      validate: value => {
                        const trimmed = String(value ?? '').trim();
                        if (
                          trimmed.length < 2 ||
                          trimmed.includes('@') ||
                          !/^[\p{L}][\p{L}\s.'-]*$/u.test(trimmed)
                        ) {
                          return 'Please enter a valid name';
                        }
                        return true;
                      },
                    }}
                    errors={errors}
                    className='mb-0'
                  />
                  <CustomInput
                    name='userEmail'
                    placeholder='Enter your email (e.g., name@example.com)'
                    type='email'
                    defaultValue={email === 'user@example.com' ? '' : email}
                    register={register}
                    rules={{
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address',
                      },
                    }}
                    errors={errors}
                    className='mb-0'
                  />
                  <input type='hidden' {...register('userMobile')} />
                  <input type='hidden' {...register('countryCode')} />
                  <div className='flex gap-2 items-start'>
                    <div className='w-[110px]'>
                      <CountryCodeSelect
                        value={watch('countryCode') || '+91'}
                        onValueChange={dialCode => {
                          setValue('countryCode', dialCode);
                          const digits = watch('phoneDigits') || '';
                          const combined = dialCode + digits;
                          setValue('userMobile', combined);
                          setMobile(combined);
                        }}
                        placeholder='Select Country'
                        triggerClassName='rounded-lg border-secondary border-[1px] w-full text-sm h-10 px-3 bg-white'
                        inBottomSheet={inBottomSheet}
                      />
                    </div>
                    <div className='flex-1'>
                      <CustomInput
                        name='phoneDigits'
                        placeholder='9876543210'
                        type='tel'
                        register={register}
                        rules={{
                          required: 'Mobile number is required',
                          pattern: {
                            value: /^\d{7,14}$/,
                            message: 'Invalid phone number (digits only)',
                          },
                          onChange: e => {
                            const code = watch('countryCode') || '+91';
                            const combined = code + e.target.value;
                            setValue('userMobile', combined);
                            setMobile(combined);
                          },
                        }}
                        errors={errors}
                        className='mb-0'
                      />
                    </div>
                  </div>
                </div>

                {/* Terms and Conditions (checkbox) - only when no booking ID; with booking ID, T&C link is shown above */}
                {termsAndConditions && !hasBookingIdInUrl && (
                  <div className='mt-4'>
                    <label className='flex items-center gap-2 text-sm text-gray-700 cursor-pointer'>
                      <input
                        type='checkbox'
                        className='h-4 w-4 border-gray-300 rounded'
                        {...register('acceptTerms', {
                          required: 'You must accept the Terms and Conditions',
                        })}
                      />
                      <span>
                        I accept the{' '}
                        <Dialog>
                          <DialogTrigger asChild>
                            <button
                              type='button'
                              className='text-blue-600 underline hover:text-blue-700'
                            >
                              Terms and Conditions
                            </button>
                          </DialogTrigger>
                          <DialogContent className='sm:!max-w-3xl md:!max-w-4xl lg:!max-w-5xl'>
                            <DialogHeader>
                              <DialogTitle className='text-2xl font-semibold text-black'>
                                Terms and Conditions
                              </DialogTitle>
                            </DialogHeader>
                            <div className='h-px bg-gray-200 -mx-6 mb-2' />
                            <div className='max-h-[70vh] overflow-auto text-sm text-gray-800 leading-6'>
                              <TermsAndConditionsContent
                                html={termsAndConditions}
                              />
                            </div>
                            <div className='mt-4 flex items-center justify-end gap-3'>
                              <DialogClose asChild>
                                <button
                                  type='button'
                                  className='btn btn-secondary px-4 py-2'
                                >
                                  Close
                                </button>
                              </DialogClose>
                              <DialogClose asChild>
                                <button
                                  type='button'
                                  className='btn btn-primary px-4 py-2'
                                  onClick={() => {
                                    setValue('acceptTerms', true, {
                                      shouldDirty: true,
                                      shouldTouch: true,
                                      shouldValidate: true,
                                    });
                                    clearErrors('acceptTerms');
                                  }}
                                >
                                  Accept
                                </button>
                              </DialogClose>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </span>
                    </label>
                    {errors.acceptTerms && (
                      <p className='text-red-500 text-xs mt-1'>
                        {errors.acceptTerms.message as string}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Submit Button - hidden when page is loaded with a booking ID (e.g. viewing existing booking) */}
            {!hasBookingIdInUrl && (
              <>
                {showUserDetails ? (
                  <CustomButton
                    label={loading ? 'Sending...' : 'Send Enquiry'}
                    variant='primary'
                    type='submit'
                    className='w-full mt-4 disabled:bg-gray-300 disabled:text-gray-700 disabled:border-gray-300 disabled:opacity-100 disabled:cursor-not-allowed'
                    disabled={
                      loading ||
                      (termsAndConditions ? !watch('acceptTerms') : false)
                    }
                    onClick={() => {}}
                  />
                ) : (
                  <CustomButton
                    label='Enquire Now'
                    variant='primary'
                    type='button'
                    className='w-full mt-4'
                    onClick={handleInitialValidation}
                  />
                )}
              </>
            )}
          </form>
        </div>
      </div>

      {/* Custom Toast */}
      {showToast && (
        <div
          className='fixed top-[30rem] right-4 z-[9999]'
          style={{ pointerEvents: 'auto' }}
        >
          <CustomToast
            message={toastConfig.message}
            type={toastConfig.type}
            onClose={() => setShowToast(false)}
            duration={0}
          />
        </div>
      )}
    </>
  );
};
